import React, { useState } from 'react';
import './WithdrawalStrategyExplainer.css';

interface WithdrawalStrategyExplainerProps {
  detailedData: any[];
  userAge: number;
  retirementAge: number;
  cppStartAge: number;
  oasStartAge: number;
}

const WithdrawalStrategyExplainer: React.FC<WithdrawalStrategyExplainerProps> = ({
  detailedData,
  userAge,
  retirementAge,
  cppStartAge,
  oasStartAge
}) => {
  const [selectedPhase, setSelectedPhase] = useState<string>('all');

  if (!detailedData || detailedData.length === 0) {
    return <div className="strategy-explainer">No data available</div>;
  }

  // Define retirement phases
  const phases = [
    {
      id: 'early-retirement',
      name: 'Early Retirement (Before CPP/OAS)',
      ageRange: `${retirementAge}-${Math.min(cppStartAge, oasStartAge) - 1}`,
      startAge: retirementAge,
      endAge: Math.min(cppStartAge, oasStartAge) - 1,
      icon: '🌅',
      strategy: 'RRSP Drawdown & TFSA Preservation',
      explanation: [
        `You're in early retirement before government benefits begin (CPP starts at ${cppStartAge}, OAS at ${oasStartAge}).`,
        'RRSP/RRIF withdrawals fund your lifestyle while income is low, minimizing taxes.',
        'TFSA is preserved for tax-free growth and emergency flexibility.',
        'This strategy fills lower tax brackets efficiently before CPP/OAS kick in.'
      ]
    },
    {
      id: 'government-benefits',
      name: 'Government Benefits Phase',
      ageRange: `${Math.min(cppStartAge, oasStartAge)}-70`,
      startAge: Math.min(cppStartAge, oasStartAge),
      endAge: 70,
      icon: '🏛️',
      strategy: 'Balanced Withdrawal with Government Income',
      explanation: [
        `CPP and OAS provide baseline income (CPP: ${cppStartAge}, OAS: ${oasStartAge}).`,
        'RRSP/RRIF withdrawals supplement government benefits.',
        'Strategy focuses on staying within optimal tax brackets.',
        'TFSA remains available for major expenses without tax impact.'
      ]
    },
    {
      id: 'rrif-minimum',
      name: 'RRIF Minimum Phase',
      ageRange: '71+',
      startAge: 71,
      endAge: 999,
      icon: '📜',
      strategy: 'Required Minimum Withdrawals',
      explanation: [
        'At 71, RRSP converts to RRIF with mandatory minimum withdrawals.',
        'RRIF minimums start at ~5.28% and increase with age.',
        'Government income (CPP/OAS) + RRIF minimums may cover all needs.',
        'Excess RRIF withdrawals can be reinvested in TFSA if room available.',
        'Non-registered accounts used if RRIF + government income insufficient.'
      ]
    }
  ];

  // Analyze actual data for a given phase
  const analyzePhase = (phase: any) => {
    const phaseData = detailedData.filter(d =>
      d.age >= phase.startAge && d.age <= phase.endAge
    );

    if (phaseData.length === 0) return null;

    const avgRRSPWithdrawal = phaseData.reduce((sum, d) => sum + (d.rrsp_withdrawal || 0), 0) / phaseData.length;
    const avgRRIFWithdrawal = phaseData.reduce((sum, d) => sum + (d.rrif_withdrawal || 0), 0) / phaseData.length;
    const avgTFSAWithdrawal = phaseData.reduce((sum, d) => sum + (d.tfsa_withdrawal || 0), 0) / phaseData.length;
    const avgNonRegWithdrawal = phaseData.reduce((sum, d) => sum + (d.non_registered_withdrawal || 0), 0) / phaseData.length;
    const avgCPP = phaseData.reduce((sum, d) => sum + (d.cpp_income || 0), 0) / phaseData.length;
    const avgOAS = phaseData.reduce((sum, d) => sum + (d.oas_income || 0), 0) / phaseData.length;
    const avgPension = phaseData.reduce((sum, d) => sum + (d.pension_income || 0), 0) / phaseData.length;
    const avgTaxes = phaseData.reduce((sum, d) => sum + (d.taxes || 0), 0) / phaseData.length;

    const totalIncome = avgRRSPWithdrawal + avgRRIFWithdrawal + avgTFSAWithdrawal +
                       avgNonRegWithdrawal + avgCPP + avgOAS + avgPension;
    const taxRate = totalIncome > 0 ? (avgTaxes / totalIncome) * 100 : 0;

    return {
      avgRRSPWithdrawal,
      avgRRIFWithdrawal,
      avgTFSAWithdrawal,
      avgNonRegWithdrawal,
      avgCPP,
      avgOAS,
      avgPension,
      avgTaxes,
      totalIncome,
      taxRate
    };
  };

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(val);

  const renderPhaseCard = (phase: any) => {
    const analysis = analyzePhase(phase);

    if (!analysis) {
      return (
        <div key={phase.id} className="phase-card disabled">
          <div className="phase-header">
            <span className="phase-icon">{phase.icon}</span>
            <div>
              <h4>{phase.name}</h4>
              <span className="age-range">Age {phase.ageRange}</span>
            </div>
          </div>
          <p className="phase-note">You won't reach this phase (life expectancy: {detailedData[detailedData.length - 1]?.age})</p>
        </div>
      );
    }

    const { totalIncome, taxRate, avgCPP, avgOAS, avgPension, avgRRSPWithdrawal,
            avgRRIFWithdrawal, avgTFSAWithdrawal, avgNonRegWithdrawal } = analysis;

    return (
      <div key={phase.id} className="phase-card">
        <div className="phase-header">
          <span className="phase-icon">{phase.icon}</span>
          <div>
            <h4>{phase.name}</h4>
            <span className="age-range">Age {phase.ageRange}</span>
          </div>
        </div>

        <div className="phase-strategy">
          <strong>Strategy:</strong> {phase.strategy}
        </div>

        <div className="phase-explanation">
          {phase.explanation.map((text: string, idx: number) => (
            <p key={idx}>{text}</p>
          ))}
        </div>

        <div className="phase-numbers">
          <div className="number-item">
            <div className="number-label">Avg Annual Income</div>
            <div className="number-value">{formatCurrency(totalIncome)}</div>
          </div>
          <div className="number-item">
            <div className="number-label">Effective Tax Rate</div>
            <div className="number-value">{taxRate.toFixed(1)}%</div>
          </div>
        </div>

        <details className="phase-breakdown">
          <summary>Income Breakdown</summary>
          <div className="breakdown-content">
            {avgCPP > 100 && (
              <div className="breakdown-item">
                <span>CPP Income:</span>
                <strong>{formatCurrency(avgCPP)}</strong>
              </div>
            )}
            {avgOAS > 100 && (
              <div className="breakdown-item">
                <span>OAS Income:</span>
                <strong>{formatCurrency(avgOAS)}</strong>
              </div>
            )}
            {avgPension > 100 && (
              <div className="breakdown-item">
                <span>Pension Income:</span>
                <strong>{formatCurrency(avgPension)}</strong>
              </div>
            )}
            {avgRRSPWithdrawal > 100 && (
              <div className="breakdown-item">
                <span>RRSP Withdrawals:</span>
                <strong>{formatCurrency(avgRRSPWithdrawal)}</strong>
              </div>
            )}
            {avgRRIFWithdrawal > 100 && (
              <div className="breakdown-item">
                <span>RRIF Withdrawals:</span>
                <strong>{formatCurrency(avgRRIFWithdrawal)}</strong>
              </div>
            )}
            {avgTFSAWithdrawal > 100 && (
              <div className="breakdown-item">
                <span>TFSA Withdrawals:</span>
                <strong>{formatCurrency(avgTFSAWithdrawal)}</strong>
              </div>
            )}
            {avgNonRegWithdrawal > 100 && (
              <div className="breakdown-item">
                <span>Non-Registered:</span>
                <strong>{formatCurrency(avgNonRegWithdrawal)}</strong>
              </div>
            )}
          </div>
        </details>
      </div>
    );
  };

  return (
    <div className="strategy-explainer">
      <div className="explainer-header">
        <h3>Withdrawal Strategy Explained</h3>
        <p>Understanding WHY your plan withdraws from different accounts at different times</p>
      </div>

      <div className="key-principles">
        <h4>Core Principles</h4>
        <div className="principles-grid">
          <div className="principle-card">
            <div className="principle-icon">📉</div>
            <div className="principle-content">
              <strong>Tax Minimization</strong>
              <p>Withdraw from taxable accounts (RRSP/RRIF) when income is low to use lower tax brackets efficiently</p>
            </div>
          </div>
          <div className="principle-card">
            <div className="principle-icon">⏰</div>
            <div className="principle-content">
              <strong>Timing Optimization</strong>
              <p>Delay CPP/OAS when possible to earn bonuses (up to 42% for CPP, 36% for OAS)</p>
            </div>
          </div>
          <div className="principle-card">
            <div className="principle-icon">🛡️</div>
            <div className="principle-content">
              <strong>TFSA Preservation</strong>
              <p>Keep TFSA growing tax-free as long as possible - it's your most flexible asset</p>
            </div>
          </div>
          <div className="principle-card">
            <div className="principle-icon">📜</div>
            <div className="principle-content">
              <strong>RRIF Compliance</strong>
              <p>Meet mandatory minimums starting at 71 (5.28% at 71, increasing with age)</p>
            </div>
          </div>
        </div>
      </div>

      <div className="phases-section">
        <h4>Your Retirement Phases</h4>
        <div className="phases-container">
          {phases.map(phase => renderPhaseCard(phase))}
        </div>
      </div>

      <div className="strategy-tips">
        <h4>💡 Strategy Tips</h4>
        <ul>
          <li><strong>Flexibility is key:</strong> This is an optimization, not a rigid rule. Adjust for life events.</li>
          <li><strong>TFSA is your emergency fund:</strong> Tax-free withdrawals anytime without affecting income.</li>
          <li><strong>Income splitting:</strong> If married, consider pension income splitting to reduce taxes.</li>
          <li><strong>Review annually:</strong> Market changes, tax law changes, and life changes require updates.</li>
        </ul>
      </div>
    </div>
  );
};

export default WithdrawalStrategyExplainer;
